/*
 * How this browser can install Beacon, which decides what "Install Beacon" does and whether it
 * shows at all. Chromium (Android, and Chrome or Edge on a desk) offers its own prompt, which the
 * page keeps from `beforeinstallprompt` and opens on a press. iOS has no prompt: Safari, and since
 * iOS 16.4 the other browsers there, add a page to the home screen from the Share sheet, so Beacon
 * shows the two steps. A browser inside another app (Slack's, where most Beacon links are opened,
 * Instagram's, Facebook's) can't install anything, so Beacon says to open it in the phone's own
 * browser. Slack on an iPhone mostly opens links in Safari's own view, though, whose user agent is
 * Safari's: it gets the two steps, and the sheet says what to do when Add to Home Screen isn't
 * there. Once installed, nothing about installing shows.
 */

/**
 * The ways to install: `installed` (it is the installed app), `prompt` (the browser's own),
 * `ios-share` (the Share sheet's two steps), `open-in-browser` (an in-app browser, which can't), or
 * `unsupported` (nothing to offer: Firefox or Safari on a desk, or Chromium before it offers its
 * prompt).
 */
export type InstallRoute = "installed" | "prompt" | "ios-share" | "open-in-browser" | "unsupported"

/** The marks an in-app browser leaves in its user agent: Slack's, Meta's, LinkedIn's and others'. */
const inAppBrowser =
  /\bSlack\b|FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|\bLine\/|MicroMessenger|Twitter/

/** The iOS release in an iPhone's or iPad's user agent ("OS 17_4"), as [major, minor]. */
function iosVersionOf(ua: string): [number, number] | undefined {
  const found = /OS (\d+)_(\d+)/.exec(ua)
  return found ? [Number(found[1]), Number(found[2])] : undefined
}

/**
 * Whether the device runs iOS or iPadOS. An iPad asks for the desktop site, so it names itself a
 * Mac, and only its touch screen (`maxTouchPoints`) gives it away.
 */
function onIos(ua: string, maxTouchPoints: number) {
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1)
}

/**
 * How this browser can install Beacon. `ua` is its user agent; `standalone` whether it already
 * runs as the installed app (`display-mode: standalone`, or iOS's `navigator.standalone`);
 * `hasPrompt` whether Chromium has handed over its install prompt; `maxTouchPoints` the screen's,
 * which tells an iPad from a Mac.
 */
export function installRoute({
  ua,
  standalone,
  hasPrompt,
  maxTouchPoints = 0,
}: {
  ua: string
  standalone: boolean
  hasPrompt: boolean
  maxTouchPoints?: number
}): InstallRoute {
  if (standalone) return "installed"
  if (hasPrompt) return "prompt"
  if (inAppBrowser.test(ua)) return "open-in-browser"
  if (onIos(ua, maxTouchPoints)) {
    // Every browser on iOS is Safari's engine; Chrome, Firefox and Edge name themselves apart.
    const otherBrowser = /CriOS|FxiOS|EdgiOS/.test(ua)
    // A web view inside another app says neither Safari nor another browser's name.
    if (!otherBrowser && !/Safari\//.test(ua)) return "open-in-browser"
    if (!otherBrowser) return "ios-share"
    const [major = 0, minor = 0] = iosVersionOf(ua) ?? []
    return major > 16 || (major === 16 && minor >= 4) ? "ios-share" : "open-in-browser"
  }
  // Android's web view, inside another app, marks itself `; wv)`.
  if (/Android/.test(ua) && /; wv\)/.test(ua)) return "open-in-browser"
  return "unsupported"
}

/** Whether "Install Beacon" is offered: wherever there is a way to install, short of being it. */
export const offersInstall = (route: InstallRoute) =>
  route === "prompt" || route === "ios-share" || route === "open-in-browser"

/**
 * Whether Home offers installing on its card: once, on the compact layout, wherever "Install
 * Beacon" is offered, until the Member dismisses it or installs.
 */
export function homeOffers({
  route,
  compact,
  dismissed,
}: {
  route: InstallRoute
  compact: boolean
  dismissed: boolean
}) {
  return compact && !dismissed && offersInstall(route)
}
